import { createModel } from "@rematch/core";
import * as OnboardingApi from "../shared/api/Onboarding.api";
import { IApiResponseError } from "../shared/api/dtos/IApiRepsonseError";
// @ts-ignore
import { toast } from "cwb-react";
// @ts-ignore
import * as Sentry from "@sentry/react";

// Helper function to determine rosterId from profiles
const getRosterIdFromProfiles = (profiles: any[], agentId?: string): number => {
  if (agentId) {
    const matchingProfile = profiles.find(profile => 
      profile.agentId && profile.agentId.toString() === agentId
    );
    if (matchingProfile) {
      return matchingProfile.rosterId;
    }
  }
  
  const qualifiedProfile = profiles.find(profile => 
    profile.resumePermission > 2 && profile.agentId !== 65
  );
  if (qualifiedProfile) {
    return qualifiedProfile.rosterId;
  }
  
  // 3. Pick item with agentId 65
  const agent65Profile = profiles.find(profile => 
    profile.agentId === 65
  );
  if (agent65Profile) {
    return agent65Profile.rosterId;
  }
  
  // 4. Default to rosterId = 0
  return 0;
};

export interface IOnboardingState {
  isLoading: boolean;
  uploadProgress: number;
  errors: {
    uploadError?: string;
    errorMessage?: string;
  };
  uploadedPhotos: string[];
  uploadedResume: string | null;
  multipleProfiles: any[];
  multipleProfilesLoading: boolean;
}

const onboardingModel = createModel({
  state: {
    isLoading: false,
    uploadProgress: 0,
    errors: {},
    uploadedPhotos: [],
    uploadedResume: null,
    multipleProfiles: [],
    multipleProfilesLoading: false,
  } as IOnboardingState,
  
  reducers: {
    setIsLoading: (state: IOnboardingState, isLoading: boolean) => ({
      ...state,
      isLoading,
    }),
    setUploadProgress: (state: IOnboardingState, progress: number) => ({
      ...state,
      uploadProgress: progress,
    }),
    setErrors: (state: IOnboardingState, errors: any) => ({
      ...state,
      errors,
    }),
    addUploadedPhoto: (state: IOnboardingState, photoId: string) => ({
      ...state,
      uploadedPhotos: [...state.uploadedPhotos, photoId],
    }),
    setUploadedResume: (state: IOnboardingState, resumeId: string) => ({
      ...state,
      uploadedResume: resumeId,
    }),
    resetUpload: (state: IOnboardingState) => ({
      ...state,
      uploadProgress: 0,
      errors: {},
    }),
    setMultipleProfilesLoading: (state: IOnboardingState, loading: boolean) => ({
      ...state,
      multipleProfilesLoading: loading,
    }),
    setMultipleProfiles: (state: IOnboardingState, profiles: any[]) => ({
      ...state,
      multipleProfiles: profiles,
    }),
  },
  
  effects: (dispatch: any) => ({
    async uploadPhoto(payload: { clientId: string; file: File }, state: any) {
      dispatch.onboardingModel.setIsLoading(true);
      dispatch.onboardingModel.setErrors({});
      
      try {
        const response = await OnboardingApi.uploadPhoto(payload.clientId, payload.file);
        
        if (response.data.success) {
          dispatch.onboardingModel.addUploadedPhoto(response.data.photoUrl);
          dispatch.onboardingModel.setErrors({});
        } else {
          let errorMessage = response.data.message;
          
          if (response.data.tooLarge) {
            errorMessage = 'File is too large. Please choose a smaller file.';
          } else if (response.data.maxNumberOfPhotosExceed) {
            errorMessage = 'Maximum number of photos exceeded.';
          } else if (response.data.invalidFile) {
            errorMessage = 'Invalid file type. Please upload a valid image.';
          } else if (response.data.errors && response.data.errors.length > 0) {
            errorMessage = response.data.errors[0].message;
          }
          
          dispatch.onboardingModel.setErrors({
            uploadError: errorMessage,
          });
          
          // Throw error to indicate upload failed - let caller handle toast
          throw new Error(errorMessage);
        }
        
        return response.data;
      } catch (error) {
        Sentry.captureException(error);
        
        let apiResponseError: IApiResponseError = error as IApiResponseError;
        let errorMsg = 'Failed to upload photo. Please try again.';
        
        if (apiResponseError.message) {
          errorMsg = apiResponseError.message;
          dispatch.onboardingModel.setErrors({
            uploadError: apiResponseError.message,
          });
        } else {
          dispatch.onboardingModel.setErrors({
            uploadError: errorMsg,
          });
        }
        
        console.error('Upload error:', error);
        throw error;
      } finally {
        dispatch.onboardingModel.setIsLoading(false);
      }
    },
    
    async uploadResume(payload: { clientId: string; file: File }, state: any) {
      dispatch.onboardingModel.setIsLoading(true);
      dispatch.onboardingModel.setErrors({});
      
      try {
        const response = await OnboardingApi.uploadPhoto(payload.clientId, payload.file);
        
        if (response.data.success) {
          dispatch.onboardingModel.setUploadedResume(response.data.photoUrl);
          dispatch.onboardingModel.setErrors({});
          toast.success('Resume uploaded successfully!', {autoClose: 3000, closeOnClick: true, pauseOnFocusLoss: false});
        } else {
          let errorMessage = response.data.message;
          
          if (response.data.tooLarge) {
            errorMessage = 'Resume file is too large. Please choose a smaller file.';
          } else if (response.data.invalidFile) {
            errorMessage = 'Invalid file type. Please upload a valid PDF.';
          } else if (response.data.errors && response.data.errors.length > 0) {
            errorMessage = response.data.errors[0].message;
          }
          
          dispatch.onboardingModel.setErrors({
            uploadError: errorMessage,
          });
          
          // Throw error to indicate upload failed - let caller handle toast
          throw new Error(errorMessage);
        }
        
        return response.data;
      } catch (error) {
        Sentry.captureException(error);
        
        let apiResponseError: IApiResponseError = error as IApiResponseError;
        let errorMsg = 'Failed to upload resume. Please try again.';
        
        if (apiResponseError.message) {
          errorMsg = apiResponseError.message;
          dispatch.onboardingModel.setErrors({
            uploadError: apiResponseError.message,
          });
        } else {
          dispatch.onboardingModel.setErrors({
            uploadError: errorMsg,
          });
        }
        
        console.error('Resume upload error:', error);
        throw error;
      } finally {
        dispatch.onboardingModel.setIsLoading(false);
      }
    },
    
    async uploadResumeWithRoster(payload: { clientId: string; file: File; agentId?: string; title?: string }, state: any) {
      dispatch.onboardingModel.setIsLoading(true);
      dispatch.onboardingModel.setErrors({});
      
      try {
        const profiles = state.onboardingModel.multipleProfiles;
        const rosterId = getRosterIdFromProfiles(profiles, payload.agentId);
        const response = await OnboardingApi.uploadResume(payload.clientId, rosterId, payload.file, payload.title);
        
        if (response?.data && (response?.statusText === 'OK')) {
          dispatch.onboardingModel.setUploadedResume(response.data.pdfUrl || '');
          dispatch.onboardingModel.setErrors({});
        } else {
          let errorMessage = response.data.message;
          
          if (response.data.tooLarge) {
            errorMessage = 'Resume file is too large. Please choose a smaller file.';
          } else if (response.data.invalidFile) {
            errorMessage = 'Invalid file type. Please upload a valid PDF.';
          } else if (response.data.errors && response.data.errors.length > 0) {
            errorMessage = response.data.errors[0].message;
          }
          
          dispatch.onboardingModel.setErrors({
            uploadError: errorMessage,
          });
          
          // Throw error to indicate upload failed - let caller handle toast
          throw new Error(errorMessage);
        }
        
        return response.data;
      } catch (error) {
        Sentry.captureException(error);
        
        let apiResponseError: IApiResponseError = error as IApiResponseError;
        let errorMsg = 'Failed to upload resume. Please try again.';
        
        if (apiResponseError.message) {
          errorMsg = apiResponseError.message;
          dispatch.onboardingModel.setErrors({
            uploadError: apiResponseError.message,
          });
        } else {
          dispatch.onboardingModel.setErrors({
            uploadError: errorMsg,
          });
        }
        
        console.error('Resume upload error:', error);
        throw error;
      } finally {
        dispatch.onboardingModel.setIsLoading(false);
      }
    },
    
    async getMultipleProfiles(clientId: string, state: any) {
      dispatch.onboardingModel.setMultipleProfilesLoading(true);
      dispatch.onboardingModel.setErrors({});
      
      try {
        const response = await OnboardingApi.getMultipleProfiles(clientId);
        
        if (response.data) {
          dispatch.onboardingModel.setMultipleProfiles(response.data);
          dispatch.onboardingModel.setErrors({});
        }
        
        return response.data;
      } catch (error) {
        Sentry.captureException(error);
        
        let apiResponseError: IApiResponseError = error as IApiResponseError;
        let errorMsg = 'Failed to load profiles. Please try again.';
        
        if (apiResponseError.message) {
          errorMsg = apiResponseError.message;
        }
        
        dispatch.onboardingModel.setErrors({
          errorMessage: errorMsg,
        });
        
        console.error('Get multiple profiles error:', error);
        throw error;
      } finally {
        dispatch.onboardingModel.setMultipleProfilesLoading(false);
      }
    },
  }),
});

export default onboardingModel;